import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import provider components (lazy-loaded for performance)
const ProviderView = React.lazy(() => import("./ProviderView"));
const EditProvider = React.lazy(() => import("./EditProvider"));
const DiscoverProviders = React.lazy(() => import("./DiscoverProviders"));

/**
 * Provider Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (provider/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/provider/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const providerModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "provider",
    name: "Provider Profile",
    description: "Professional profile page showcasing services, experience, and achievements",
    version: "1.0.0",
    enabled: true,
    category: "professional",
    order: 6,
    icon: "🎯",
    color: "#6B46C1",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/provider",
            component: ProviderView,
            permissionLevel: "view",
        },
        {
            path: "/provider/edit",
            component: EditProvider,
            permissionLevel: "edit",
        },
        {
            path: "/providers/discover",
            component: DiscoverProviders,
            public: true, // Public access - no auth required
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "My Provider Profile",
            path: "/provider",
            icon: "🎯",
            permissionLevel: "view",
        },
        {
            label: "Discover Providers",
            path: "/providers/discover",
            icon: "🌟",
            permissionLevel: "view",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/provider/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Edit Profile",
            path: "/provider/edit",
            icon: "✏️",
            description: "Edit your professional profile"
        },
        {
            label: "Discover Providers",
            path: "/providers/discover",
            icon: "🔍",
            description: "Find amazing professionals and service providers"
        }
    ],

    // CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "Production-ready provider profile system",
                "Professional profile pages with SEO optimization",
                "Provider discovery and search functionality",
                "IPFS image storage integration",
                "Custom URL slugs for public profiles",
                "Skills, experience, and portfolio showcases",
                "Mobile-responsive design"
            ],
            notes: "Official stable release with comprehensive professional profile features."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-10-31",
            title: "IPFS Server Integration",
            features: [
                "Added own IPFS server support (3 lines)",
                "Enhanced media storage for profile images",
                "Better control over file hosting"
            ],
            notes: "Self-hosted IPFS integration for profile media."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-09-20",
            title: "Theme System Migration",
            features: [
                "Full light/dark mode support (88 insertions)",
                "Enhanced DiscoverProviders with theme awareness (94 lines)",
                "Updated EditProvider color modes (10 lines)",
                "Improved ProviderView themes (52 lines)",
                "Consistent visual design system"
            ],
            notes: "Complete theme system implementation for better user experience."
        },
        {
            version: "0.3.0",
            releaseDate: "2023-08-08",
            title: "UI Enhancements & Consistency",
            features: [
                "Enhanced DiscoverProviders UI (287 insertions)",
                "Improved brand configuration integration (4 lines)",
                "Better responsive design",
                "Enhanced visual consistency"
            ],
            notes: "UI improvements and brand consistency updates."
        },
        {
            version: "0.2.0",
            releaseDate: "2023-07-19",
            title: "Provider Discovery System",
            features: [
                "Created DiscoverProviders component (629 lines)",
                "Enhanced EditProvider responsiveness (119 insertions)",
                "Improved ProviderView UI (213 insertions)",
                "Updated routing for discovery (18 lines)",
                "Added comprehensive integration documentation (65 lines)",
                "Better button styling and consistency"
            ],
            notes: "Major feature expansion with provider discovery and search capabilities."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-07-18",
            title: "Initial Provider Profile System",
            features: [
                "Created EditProvider component (2,835 lines)",
                "Created ProviderView component (1,265 lines)",
                "Module configuration and routing (63 lines)",
                "Created integration guide (174 lines)",
                "Professional profile creation and editing",
                "Role management and experience tracking",
                "Skills assessment with proficiency levels",
                "Portfolio and testimonials support",
                "Public/private profile visibility",
                "URL slug functionality"
            ],
            notes: "Initial implementation with 4,163 lines of professional profile code."
        }
    ],

    // ROADMAP
    roadmap: []

};

export default providerModuleConfig;
